import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["blob-sink-actions"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      blobSinkActionsList: build.query<
        BlobSinkActionsListApiResponse,
        BlobSinkActionsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/`,
        }),
        providesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsRetrieve: build.query<
        BlobSinkActionsRetrieveApiResponse,
        BlobSinkActionsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/${queryArg.actionId}/`,
        }),
        providesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsApprove: build.mutation<
        BlobSinkActionsApproveApiResponse,
        BlobSinkActionsApproveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/${queryArg.actionId}/approve/`,
          method: "POST",
        }),
        invalidatesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsReject: build.mutation<
        BlobSinkActionsRejectApiResponse,
        BlobSinkActionsRejectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/${queryArg.actionId}/reject/`,
          method: "POST",
          body: queryArg.rejectActionRequest,
        }),
        invalidatesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsRemove: build.mutation<
        BlobSinkActionsRemoveApiResponse,
        BlobSinkActionsRemoveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/${queryArg.actionId}/remove/`,
          method: "POST",
        }),
        invalidatesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsRepush: build.mutation<
        BlobSinkActionsRepushApiResponse,
        BlobSinkActionsRepushApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/${queryArg.blobId}/sink-actions/${queryArg.actionId}/repush/`,
          method: "POST",
        }),
        invalidatesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsRecheckMatter: build.mutation<
        BlobSinkActionsRecheckMatterApiResponse,
        BlobSinkActionsRecheckMatterApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/sink-actions/recheck/`,
          method: "POST",
        }),
        invalidatesTags: ["blob-sink-actions"],
      }),
      blobSinkActionsTree: build.query<
        BlobSinkActionsTreeApiResponse,
        BlobSinkActionsTreeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/blobs/sink-actions/tree/`,
          params: {
            target_system: queryArg.targetSystem,
          },
        }),
        providesTags: ["blob-sink-actions"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type BlobSinkActionsListApiResponse =
  /** status 200  */ BlobSinkActionSchema[];
export type BlobSinkActionsListApiArg = {
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
};
export type BlobSinkActionsRetrieveApiResponse =
  /** status 200  */ BlobSinkActionSchema;
export type BlobSinkActionsRetrieveApiArg = {
  /** ID of the sink action */
  actionId: string;
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
};
export type BlobSinkActionsApproveApiResponse =
  /** status 200  */ BlobSinkActionSchema;
export type BlobSinkActionsApproveApiArg = {
  /** ID of the sink action to approve */
  actionId: string;
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
};
export type BlobSinkActionsRejectApiResponse =
  /** status 200  */ BlobSinkActionSchema;
export type BlobSinkActionsRejectApiArg = {
  /** ID of the sink action to reject */
  actionId: string;
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
  rejectActionRequest: RejectActionRequest;
};
export type BlobSinkActionsRemoveApiResponse =
  /** status 200  */ BlobSinkActionSchema;
export type BlobSinkActionsRemoveApiArg = {
  /** ID of the sink action to remove */
  actionId: string;
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
};
export type BlobSinkActionsRepushApiResponse =
  /** status 200  */ BlobSinkActionSchema;
export type BlobSinkActionsRepushApiArg = {
  /** ID of the sink action to repush */
  actionId: string;
  /** ID of the blob */
  blobId: string;
  /** ID of the matter */
  matterId: string;
};
export type BlobSinkActionsRecheckMatterApiResponse =
  /** status 200  */ RecheckBlobsResponseSchema;
export type BlobSinkActionsRecheckMatterApiArg = {
  /** ID of the matter to recheck blobs for */
  matterId: string;
};
export type BlobSinkActionsTreeApiResponse =
  /** status 200  */ SinkFileStructureSchema;
export type BlobSinkActionsTreeApiArg = {
  /** ID of the matter to retrieve file structure for */
  matterId: string;
  /** The target system to retrieve file structure from (e.g., FILEVINE) */
  targetSystem: "CLIO" | "FILEVINE" | "NEOS" | "SMARTADVOCATE";
};
export type BlobSinkActionStatus =
  | "PENDING"
  | "PROCESSING"
  | "AWAITING_APPROVAL"
  | "WAITING_FOR_PUSH_WINDOW"
  | "READY_TO_PUSH"
  | "PUSHING"
  | "PUSHED"
  | "FAILED"
  | "ALREADY_EXISTS"
  | "REJECTED"
  | "REMOVED";
export type BlobSinkActionTargetSystem =
  | "FILEVINE"
  | "LITIFY"
  | "NEOS"
  | "CLIO"
  | "SMARTADVOCATE"
  | "CASEPEER"
  | "MYCASE";
export type BlobSinkApprovalMode = "AUTOMATIC" | "HUMAN";
export type BlobSinkApprovalStatus = "PENDING" | "APPROVED" | "REJECTED";
export type BlobSinkActionSchema = {
  id: string | string;
  status: BlobSinkActionStatus;
  blob_id: string | string;
  target_system: BlobSinkActionTargetSystem;
  target_path?: string | null;
  target_name?: string | null;
  target_reasoning?: string | null;
  target_confidence?: number | null;
  last_workflow_run_id?: string | null;
  filevine_document_id?: (string | string) | null;
  neos_document_id?: (string | string) | null;
  clio_document_id?: (string | string) | null;
  smartadvocate_document_id?: (string | string) | null;
  external_document_id?: string | null;
  approval_mode: BlobSinkApprovalMode;
  approval_status: BlobSinkApprovalStatus;
  approved_by_id?: (string | string) | null;
  approved_at?: string | null;
  metadata: {
    [key: string]: any;
  };
};
export type RejectActionRequest = {
  reason?: string | null;
};
export type RecheckBlobsResponseSchema = {
  blobs_checked: number;
  actions_created: number;
  blobs_with_actions: string[];
  blobs_skipped: string[];
};
export type SinkFileNodeSchema = {
  /** Discriminator field to identify this as a file node. */
  type?: "file";
  /** The file name. */
  name: string;
  /** The unique identifier for this file in the external system. */
  external_id?: string | null;
  /** The MIME type of the file (e.g., 'application/pdf'). */
  mime_type?: string | null;
  /** The file size in bytes, if available. */
  size_bytes?: number | null;
  /** When the file was created in the external system. */
  created_at?: string | null;
  /** When the file was last modified in the external system. */
  modified_at?: string | null;
  /** The linked Finch blob ID, if this file has been imported into Finch. */
  blob_id?: string | null;
  /** Whether this file has been imported into Finch as a blob. */
  is_synced?: boolean;
  /** The internal DropboxDocument ID, used for import operations. */
  internal_id?: string | null;
};
export type SinkFolderNodeSchema = {
  /** Discriminator field to identify this as a folder node. */
  type?: "folder";
  /** The folder name. */
  name: string;
  /** The unique identifier for this folder in the external system. */
  external_id?: string | null;
  /** The contents of this folder - can be files or nested folders. */
  children: (
    | ({
        type: "file";
      } & SinkFileNodeSchema)
    | ({
        type: "folder";
      } & SinkFolderNodeSchema)
  )[];
};
export type SinkFileStructureSchema = {
  /** The internal matter ID this file structure belongs to. */
  matter_id: string | string;
  /** The sink system this file structure was retrieved from. */
  target_system: BlobSinkActionTargetSystem;
  /** The root-level nodes (files and folders) in the matter's file structure. */
  root: (
    | ({
        type: "file";
      } & SinkFileNodeSchema)
    | ({
        type: "folder";
      } & SinkFolderNodeSchema)
  )[];
  /** The matter/project ID in the external system (e.g., Filevine project ID). */
  external_matter_id?: string | null;
  /** When this file structure was retrieved from the external system. */
  retrieved_at: string;
};
export const {
  useBlobSinkActionsListQuery,
  useBlobSinkActionsRetrieveQuery,
  useBlobSinkActionsApproveMutation,
  useBlobSinkActionsRejectMutation,
  useBlobSinkActionsRemoveMutation,
  useBlobSinkActionsRepushMutation,
  useBlobSinkActionsRecheckMatterMutation,
  useBlobSinkActionsTreeQuery,
} = injectedRtkApi;
